// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/lib/form/use-record-editor.tsx ve use-kirli.ts.
// Fark: ekran her zaman react-hook-form + zod kullanır (elle `useState` kirlilik izleme yok); doğrulama
// RHF çözücüsündedir ve sunucunun alan hataları (`errors[].field`) aynı `formState.errors`'a yazılır.
// Gezinme sonrası yönlendirme, kilit ve sekme kaydı kapsam dışıdır; sürüm zinciri `use-record-version`.
import { useState, type BaseSyntheticEvent, type ReactNode } from 'react'
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form'

import { useToastStack } from '@/components/feedback/toast-stack'
import { splitServerErrors } from '@/lib/form/server-errors'
import type { RecordVersion } from '@/lib/form/use-record-version'
import { useLeaveGuard } from '@/lib/form/use-leave-guard'
import { messages } from '@/lib/messages'
import { ApiError, toProblem } from '@/lib/problem'

export interface RecordEditorOptions<TValues extends FieldValues, TResult> {
  form: UseFormReturn<TValues>
  /** Doğrulanmış değerlerle yazma isteği. Sürümlü uç `version.current()` gönderir. */
  submit: (values: TValues) => Promise<TResult>
  /** Başarılı kayıttan sonra (form temizlendikten sonra): sürüm ilerletme, toast, kapatma. */
  onSaved?: (result: TResult, values: TValues) => void
  /** Kaydın sürüm zinciri; bayat yazmanın 409'u onun uyarısına gider (Faz 1). */
  version?: Pick<RecordVersion, 'catchConflict'>
  /**
   * Sunucu alan yolu → form alanı (ör. `'leave.bitis': 'endDate'`); aynı adlı alanlar eşlenmeden düşer.
   * Yol o anki değerlere bağlıysa (sözlük anahtarı → liste satırı) fonksiyon verilir.
   */
  serverFields?:
    | Partial<Record<string, Path<TValues>>>
    | ((path: string, values: TValues) => Path<TValues> | undefined)
  /** Ekranın özel hatası (ör. `firm_ambiguous`); `true` dönerse başka bir şey gösterilmez. */
  onError?: (error: unknown) => boolean
  /** Genel hata toast'ının başlığı. */
  failedTitle?: string
}

export interface RecordEditor {
  /** Formun `onSubmit`'i ya da düğmenin `onClick`'i: doğrular, geçerse yazar. */
  save: (event?: BaseSyntheticEvent) => Promise<void>
  saving: boolean
  dirty: boolean
  /** Kapatma/İptal: kirliyse "Kaydedilmemiş değişiklikler" onayı sorar. */
  requestClose: (close: () => void) => void
  /** Kaydetmeden çıkış onayı; ekranın JSX'ine konur. */
  dialog: ReactNode
}

/**
 * Tek kayıt formunun kaydetme akışı. Doğrulama hatası ve sunucunun 422 alan hataları alanın altında
 * (`formState.errors`), bayat sürüm 409'u Faz 1'in uyarısında, kalanı toast'ta görünür. Başarıda form
 * kaydedilen değerlerle yeniden kurulur (kirli değil) ve `onSaved` çalışır.
 */
export function useRecordEditor<TValues extends FieldValues, TResult>(
  options: RecordEditorOptions<TValues, TResult>,
): RecordEditor {
  const { form, submit, onSaved, version, serverFields = {}, onError, failedTitle } = options
  const { toast } = useToastStack()
  const [saving, setSaving] = useState(false)
  const dirty = form.formState.isDirty
  const guard = useLeaveGuard(dirty, () => form.reset())

  function fail(error: unknown) {
    if (onError?.(error)) return
    if (version?.catchConflict(error)) return
    if (error instanceof ApiError && error.fieldErrors.length > 0) {
      const values = form.getValues()
      const known = (path: string): path is Path<TValues> => readPath(values, path) !== undefined
      const fields =
        typeof serverFields === 'function'
          ? Object.fromEntries(
              error.fieldErrors.map(({ field }) => [field, serverFields(field, values)]),
            )
          : serverFields
      const { byField, rest } = splitServerErrors(error.fieldErrors, fields, known)
      byField.forEach(([field, message], index) =>
        form.setError(field, { type: 'server', message }, { shouldFocus: index === 0 }),
      )
      if (rest.length === 0) return
      toast({
        type: 'error',
        title: failedTitle ?? messages.form.saveFailed,
        description: rest.join(' '),
      })
      return
    }
    toast({
      type: 'error',
      title: failedTitle ?? messages.form.saveFailed,
      description: toProblem(error).detail ?? undefined,
    })
  }

  const save = form.handleSubmit(async (values) => {
    setSaving(true)
    try {
      const result = await submit(values)
      form.reset(values)
      onSaved?.(result, values)
    } catch (error) {
      fail(error)
    } finally {
      setSaving(false)
    }
  })

  return { save, saving, dirty, requestClose: guard.confirm, dialog: guard.dialog }
}

/**
 * Tüm değeri birden döndüren alan bileşenlerini (`onChange(next)`) RHF'ye bağlar: yalnız değişen
 * * üst düzey alanlar yazılır, kirli işaretlenir; gönderim denendiyse ya da hata varsa yeniden doğrulanır.
 */
export function assignValues<TValues extends FieldValues>(
  form: UseFormReturn<TValues>,
  next: TValues,
): void {
  const current = form.getValues()
  const validate = form.formState.isSubmitted || Object.keys(form.formState.errors).length > 0
  for (const key of Object.keys(next) as Array<Path<TValues> & keyof TValues>) {
    if (Object.is(current[key], next[key])) continue
    form.setValue(key, next[key], { shouldDirty: true, shouldValidate: validate })
  }
}

/** `a.b.0.c` yolundaki değer (form değerlerinde var mı diye bakmak için). */
function readPath(values: unknown, path: string): unknown {
  let current = values
  for (const key of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}
